import { errorMessage } from "./pb";

interface Async<T> {
  data: T | null;
  error: string;
  loading: boolean;
  reload: () => Promise<void>;
  set: (value: T | null) => void;
}

/**
 * Carga datos y expone el resultado, el error, el estado de carga y una función para
 * volver a consultar. El efecto de Svelte repite la petición cuando cambia un valor
 * reactivo leído al iniciarla.
 *
 * Cada petición recibe un número de turno. Solo la última puede actualizar el estado,
 * para que una respuesta lenta anterior no sustituya datos más recientes.
 *
 * Ejemplo: const apps = useAsync(() => api<AppRecord[]>("/api/apps"));
 */
export function useAsync<T>(fn: () => Promise<T>): Async<T> {
  let data = $state<T | null>(null);
  let error = $state("");
  let loading = $state(true);

  /** Sube con cada petición; solo contesta la última. */
  let turn = 0;

  async function run() {
    const mine = ++turn;
    loading = true;
    try {
      const result = await fn();
      if (mine !== turn) return;
      data = result;
      error = "";
    } catch (err) {
      if (mine !== turn) return;
      error = errorMessage(err);
    } finally {
      if (mine === turn) loading = false;
    }
  }

  $effect(() => {
    void run();
  });

  return {
    get data() {
      return data;
    },
    get error() {
      return error;
    },
    get loading() {
      return loading;
    },
    /** Vuelve a pedirlo sin que haya cambiado nada. */
    reload: run,
    set(value: T | null) {
      data = value;
    },
  };
}
